import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { FaInstagram, FaTiktok, FaYoutube } from "react-icons/fa";
import { db } from "../firebase";

const platforms = [
  {
    id: "youtube",
    label: "YouTube",
    icon: FaYoutube,
    iconClass: "text-red-400",
    description: "Verdieping, uitleg en langere breakdowns.",
  },
  {
    id: "tiktok",
    label: "TikTok",
    icon: FaTiktok,
    iconClass: "text-mmText",
    description: "Korte clips, observaties en trainingsmomenten.",
  },
  {
    id: "instagram",
    label: "Instagram",
    icon: FaInstagram,
    iconClass: "text-pink-400",
    description: "Carrousels, updates en dagelijkse context.",
  },
];

const platformById = Object.fromEntries(
  platforms.map((platform) => [platform.id, platform])
);

const extractYouTubeId = (value = "") => {
  try {
    const url = new URL(value);
    const host = url.hostname.replace(/^www\./, "");

    if (host === "youtu.be") {
      return url.pathname.split("/").filter(Boolean)[0] || "";
    }

    if (host === "youtube.com" || host === "m.youtube.com") {
      if (url.pathname === "/watch") {
        return url.searchParams.get("v") || "";
      }

      const [type, id] = url.pathname.split("/").filter(Boolean);
      if (type === "shorts" && id) {
        return id;
      }
    }
  } catch {
    return "";
  }

  return "";
};

const getDescriptionPreview = (value = "") => {
  const trimmed = value.trim();
  if (!trimmed) return "";

  const firstSentenceMatch = trimmed.match(/^(.+?[.!?])(\s|$)/);
  const firstSentence = firstSentenceMatch?.[1]?.trim();

  if (firstSentence && firstSentence.length <= 180) {
    return firstSentence;
  }

  if (trimmed.length <= 180) {
    return trimmed;
  }

  return `${trimmed.slice(0, 170).trim()}...`;
};

const PlatformHeading = ({ platform, aside }) => {
  const Icon = platform.icon;

  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
      <div className="flex items-start gap-4">
        <div className="h-11 w-11 rounded-xl border border-mmBorderStrong bg-mmPage flex items-center justify-center shrink-0">
          <Icon className={`text-2xl ${platform.iconClass}`} aria-hidden="true" />
        </div>
        <div>
          <h3 className="font-display uppercase tracking-widest text-mmText text-2xl md:text-3xl">
            {platform.label}
          </h3>
          <p className="text-mmTextMuted text-sm md:text-base leading-relaxed">
            {platform.description}
          </p>
        </div>
      </div>
      {aside}
    </div>
  );
};

const EmptyPlatformState = () => (
  <div className="border border-dashed border-mmBorder rounded-xl px-4 py-3 text-mmTextMuted text-sm">
    Binnenkort nieuwe updates.
  </div>
);

const ThumbnailPlaceholder = ({ platform, className = "" }) => {
  const Icon = platform.icon;

  return (
    <div
      className={`flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_top_left,rgba(255,204,1,0.22),transparent_36%),linear-gradient(135deg,#171717,#080808)] ${className}`}
    >
      <div className="text-center">
        <Icon
          className={`mx-auto mb-3 text-4xl ${platform.iconClass}`}
          aria-hidden="true"
        />
        <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-xs">
          Mission Movement
        </p>
      </div>
    </div>
  );
};

const PostThumbnail = ({ post, platform, className = "", imageClassName = "" }) => {
  if (post.thumbnailUrl) {
    return (
      <img
        src={post.thumbnailUrl}
        alt=""
        loading="lazy"
        decoding="async"
        className={`h-full w-full object-cover ${imageClassName}`}
      />
    );
  }

  return <ThumbnailPlaceholder platform={platform} className={className} />;
};

const ExternalLink = ({ href, children, className }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
    {children}
  </a>
);

const YouTubeThumbnail = ({ post, index }) => {
  const videoId = extractYouTubeId(post?.url);

  return (
    <div className="relative h-[72px] w-32 shrink-0 overflow-hidden rounded-lg border border-mmBorder bg-mmSurface sm:h-[81px] sm:w-36 xl:h-[90px] xl:w-40">
      {videoId ? (
        <img
          src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-[linear-gradient(135deg,#141414,#0b0b0b)]">
          <FaYoutube className="text-2xl text-red-400" aria-hidden="true" />
        </div>
      )}
      <span className="absolute left-1.5 top-1.5 rounded bg-black/75 px-1.5 py-0.5 font-display text-xs leading-none text-mmAccent">
        {String(index).padStart(2, "0")}
      </span>
    </div>
  );
};

const YouTubeHub = ({ posts }) => {
  const platform = platformById.youtube;
  const [activePostId, setActivePostId] = useState(null);
  const [shouldAutoplay, setShouldAutoplay] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  useEffect(() => {
    if (posts.length === 0) {
      setActivePostId(null);
      setShouldAutoplay(false);
      return;
    }

    setActivePostId((currentId) => {
      if (posts.some((post) => post.id === currentId)) {
        return currentId;
      }

      setShouldAutoplay(false);
      return posts[0].id;
    });
  }, [posts]);

  useEffect(() => {
    setDescriptionExpanded(false);
  }, [activePostId]);

  const featured = posts.find((post) => post.id === activePostId) || posts[0];
  const remainingPosts = posts.filter((post) => post.id !== featured?.id);
  const videoId = extractYouTubeId(featured?.url);
  const embedUrl = videoId
    ? `https://www.youtube.com/embed/${videoId}${shouldAutoplay ? "?autoplay=1" : ""}`
    : "";
  const moreUrl = featured?.url || "https://www.youtube.com/";
  const description = featured?.description?.trim() || "";
  const descriptionPreview = getDescriptionPreview(description);
  const canExpandDescription =
    Boolean(description) && descriptionPreview.length < description.length;

  return (
    <section className="bg-mmSurface border border-mmBorder rounded-2xl p-4 md:p-5">
      <PlatformHeading
        platform={platform}
        aside={
          posts.length > 0 && (
            <p className="text-mmTextMuted text-xs uppercase tracking-widest">
              Featured + {Math.max(posts.length - 1, 0)} updates
            </p>
          )
        }
      />

      {!featured ? (
        <EmptyPlatformState />
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,0.65fr)_minmax(340px,0.35fr)] gap-5">
          <div className="min-w-0">
            {videoId ? (
              <div className="aspect-video w-full overflow-hidden rounded-xl border border-mmBorder bg-black shadow-sm xl:max-w-[700px]">
                <iframe
                  src={embedUrl}
                  title={featured.title || "Mission Movement YouTube video"}
                  className="h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            ) : (
              <ExternalLink
                href={featured.url}
                className="group flex aspect-video w-full flex-col justify-end rounded-xl border border-mmBorder bg-mmPage p-6 shadow-sm hover:border-mmAccent transition xl:max-w-[700px]"
              >
                <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm mb-2">
                  YouTube
                </p>
                <h4 className="font-display uppercase tracking-widest text-mmText text-2xl group-hover:text-mmAccent transition">
                  {featured.title}
                </h4>
              </ExternalLink>
            )}

            <div className="mt-4 xl:max-w-[700px]">
              <ExternalLink
                href={featured.url}
                className="font-display uppercase tracking-widest text-mmText text-xl md:text-2xl hover:text-mmAccent transition break-words"
              >
                {featured.title}
              </ExternalLink>
              {description && (
                <div className="mt-2 max-w-3xl text-sm leading-relaxed text-mmTextMuted">
                  {descriptionExpanded ? (
                    <>
                      <p>{description}</p>
                      <button
                        type="button"
                        onClick={() => setDescriptionExpanded(false)}
                        className="mt-2 text-mmAccent text-sm font-semibold hover:text-mmAccentHover transition"
                      >
                        Tekst verbergen
                      </button>
                    </>
                  ) : (
                    <p>
                      {descriptionPreview}
                      {canExpandDescription && (
                        <>
                          {" "}
                          <button
                            type="button"
                            onClick={() => setDescriptionExpanded(true)}
                            className="text-mmAccent text-sm font-semibold hover:text-mmAccentHover transition"
                          >
                            ... more
                          </button>
                        </>
                      )}
                    </p>
                  )}
                </div>
              )}
              <ExternalLink
                href={moreUrl}
                className="mt-3 inline-flex text-mmAccent text-sm font-semibold"
              >
                Ga naar het YouTube kanaal van Mission Movement →
              </ExternalLink>
            </div>
          </div>

          <aside className="min-w-0">
            <div className="rounded-xl border border-mmBorder bg-mmPage p-2">
              <div className="mb-2 flex items-center justify-between px-2 py-1">
                <p className="font-display uppercase tracking-widest text-mmText text-lg">
                  Playlist
                </p>
                {remainingPosts.length > 0 && (
                  <p className="text-xs uppercase tracking-widest text-mmTextMuted">
                    {remainingPosts.length} videos
                  </p>
                )}
              </div>
              <div className="space-y-2 overflow-y-auto pb-6 pr-1 scrollbar-visible xl:h-[438px] xl:max-h-[438px]">
                {remainingPosts.length === 0 ? (
                  <div className="border border-dashed border-mmBorder rounded-xl px-4 py-3 text-mmTextMuted text-sm">
                    Meer video’s volgen binnenkort.
                  </div>
                ) : (
                  remainingPosts.map((post) => {
                    const originalIndex = posts.findIndex((item) => item.id === post.id);

                    return (
                      <button
                        key={post.id}
                        type="button"
                        onClick={() => {
                          setActivePostId(post.id);
                          setShouldAutoplay(true);
                        }}
                        className="group flex h-[88px] w-full items-center gap-3 rounded-lg border border-transparent bg-mmSurface p-2 text-left hover:border-mmAccent hover:bg-mmSurface/80 transition sm:h-[97px] xl:h-[106px]"
                      >
                        <YouTubeThumbnail post={post} index={originalIndex + 1} />
                        <span className="flex h-[72px] min-w-0 flex-col justify-center overflow-hidden sm:h-[81px] xl:h-[90px]">
                          <span className="mb-1 block text-[10px] uppercase tracking-widest text-mmTextMuted">
                            YouTube
                          </span>
                          <span className="block overflow-hidden font-display uppercase tracking-widest text-mmText text-sm leading-tight group-hover:text-mmAccent transition line-clamp-2">
                            {post.title}
                          </span>
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

          </aside>
        </div>
      )}
    </section>
  );
};

const TikTokLane = ({ posts }) => {
  const platform = platformById.tiktok;

  return (
    <section className="bg-mmSurface border border-mmBorder rounded-2xl p-5 md:p-6 xl:p-5 xl:max-h-[calc(100vh-16px)] xl:overflow-hidden">
      <PlatformHeading platform={platform} />

      {posts.length === 0 ? (
        <EmptyPlatformState />
      ) : (
        <div>
          <p className="mb-3 text-xs uppercase tracking-widest text-mmTextMuted md:text-right">
            Swipe voor meer posts →
          </p>
          <div className="relative">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4 xl:flex xl:overflow-x-auto xl:pb-3 xl:pr-8 scrollbar-visible">
              {posts.map((post) => (
                <ExternalLink
                  key={post.id}
                  href={post.url}
                  className="group flex flex-col overflow-hidden rounded-xl border border-mmBorder bg-mmPage hover:border-mmAccent hover:-translate-y-0.5 transition xl:w-[168px] 2xl:w-[180px] xl:shrink-0"
                >
                  <div className="aspect-[3/4] overflow-hidden border-b border-mmBorder bg-mmSurface">
                    <PostThumbnail post={post} platform={platform} />
                  </div>
                  <div className="flex min-h-[150px] flex-1 flex-col p-4 xl:min-h-[128px] xl:p-3">
                    <div className="min-h-0 flex-1">
                      <div className="mb-3 flex items-center gap-2 text-[10px] uppercase tracking-widest text-mmTextMuted xl:mb-2">
                        <FaTiktok className="text-mmText" aria-hidden="true" />
                        Short-form
                      </div>
                      <h4 className="font-display uppercase tracking-widest text-mmText text-base sm:text-lg group-hover:text-mmAccent transition break-words line-clamp-2 xl:text-base">
                        {post.title}
                      </h4>
                      {post.description && (
                        <p className="mt-2 text-mmTextMuted text-xs sm:text-sm leading-relaxed line-clamp-2">
                          {post.description}
                        </p>
                      )}
                    </div>
                    <span className="mt-auto pt-4 text-mmAccent text-xs sm:text-sm font-semibold xl:pt-3">
                      Bekijk TikTok →
                    </span>
                  </div>
                </ExternalLink>
              ))}
            </div>
            <div
              className="pointer-events-none absolute inset-y-0 right-0 hidden w-14 bg-gradient-to-l from-mmSurface via-mmSurface/85 to-transparent xl:block"
              aria-hidden="true"
            />
          </div>
        </div>
      )}
    </section>
  );
};

const InstagramGrid = ({ posts }) => {
  const platform = platformById.instagram;

  return (
    <section className="bg-mmSurface border border-mmBorder rounded-2xl p-5 md:p-6 xl:p-5 xl:max-h-[calc(100vh-16px)] xl:overflow-hidden">
      <PlatformHeading platform={platform} />

      {posts.length === 0 ? (
        <EmptyPlatformState />
      ) : (
        <div>
          <p className="mb-3 text-xs uppercase tracking-widest text-mmTextMuted md:text-right">
            Swipe voor meer posts →
          </p>
          <div className="relative">
            <div className="grid grid-flow-col grid-rows-2 auto-cols-[minmax(128px,42vw)] gap-px overflow-x-auto pb-3 pr-8 scrollbar-visible sm:auto-cols-[170px] md:auto-cols-[190px] xl:auto-cols-[calc((100%_-_6px)_/_7)]">
              {posts.map((post) => (
                <ExternalLink
                  key={post.id}
                  href={post.url}
                  className="group relative aspect-square overflow-hidden bg-mmPage ring-1 ring-mmBorder transition hover:z-10 hover:ring-mmAccent"
                  aria-label={post.title || "Bekijk Instagram post"}
                >
                  <PostThumbnail post={post} platform={platform} />
                  <span className="absolute inset-0 bg-black/0 transition group-hover:bg-black/15" />
                  <span className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-black/55 text-pink-300 opacity-90 transition group-hover:text-mmAccent">
                    <FaInstagram aria-hidden="true" />
                  </span>
                </ExternalLink>
              ))}
            </div>
            <div
              className="pointer-events-none absolute inset-y-0 right-0 hidden w-14 bg-gradient-to-l from-mmSurface via-mmSurface/85 to-transparent xl:block"
              aria-hidden="true"
            />
          </div>
        </div>
      )}
    </section>
  );
};

const SocialPostsSection = () => {
  const [postsByPlatform, setPostsByPlatform] = useState({
    youtube: [],
    tiktok: [],
    instagram: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    const fetchPosts = async () => {
      setLoading(true);
      setError("");

      try {
        const results = await Promise.all(
          platforms.map(async (platform) => {
            const postsQuery = query(
              collection(db, "socialPosts"),
              where("platform", "==", platform.id),
              where("published", "==", true),
              orderBy("createdAt", "desc"),
              limit(10)
            );
            const snapshot = await getDocs(postsQuery);
            return [
              platform.id,
              snapshot.docs.map((postDoc) => ({
                id: postDoc.id,
                ...postDoc.data(),
              })),
            ];
          })
        );

        if (!active) return;
        setPostsByPlatform(Object.fromEntries(results));
      } catch (err) {
        console.error("Error loading social posts:", err);
        if (active) {
          setError("Social links konden niet geladen worden.");
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchPosts();

    return () => {
      active = false;
    };
  }, []);

  const hasAnyPosts = useMemo(
    () => platforms.some((platform) => postsByPlatform[platform.id]?.length > 0),
    [postsByPlatform]
  );

  return (
    <section className="w-full bg-mmPage px-4 sm:px-6 md:px-10 lg:px-20 py-24">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl mb-14">
          <p className="font-display uppercase tracking-[0.18em] text-mmAccent text-sm md:text-base mb-3">
            SOCIAL
          </p>
          <h2 className="mm-h2 text-mmText mb-4">Laatste updates</h2>
          <p className="text-mmTextMuted text-base md:text-lg leading-relaxed">
            Korte inzichten, video’s en praktijkvoorbeelden vanuit Mission
            Movement. Gebruik deze updates als extra context naast je
            voorbereiding.
          </p>
        </div>

        {loading && (
          <div className="space-y-6">
            {platforms.map((platform) => (
              <div
                key={platform.id}
                className="bg-mmSurface border border-mmBorder rounded-2xl p-6 text-mmTextMuted"
              >
                <div className="h-5 w-40 bg-mmBorder rounded mb-4 animate-pulse" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[1, 2, 3].map((item) => (
                    <div
                      key={item}
                      className="h-28 bg-mmBorder/60 rounded-xl animate-pulse"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="border border-mmBorder rounded-2xl px-5 py-4 text-mmTextMuted">
            {error}
          </div>
        )}

        {!loading && !error && !hasAnyPosts && (
          <div className="border border-mmBorder rounded-2xl px-5 py-4 text-mmTextMuted">
            Er zijn nog geen social updates gepubliceerd.
          </div>
        )}

        {!loading && !error && hasAnyPosts && (
          <div className="space-y-10">
            <YouTubeHub posts={postsByPlatform.youtube || []} />
            <TikTokLane posts={postsByPlatform.tiktok || []} />
            <InstagramGrid posts={postsByPlatform.instagram || []} />
          </div>
        )}
      </div>
    </section>
  );
};

export default SocialPostsSection;
